import React, { useState, useRef, useEffect } from 'react';
import { marked } from 'marked';
import SEO from '../components/SEO';

const EXAMPLE_MD = `# Markdown Viewer

A **fast**, *free* online Markdown previewer.

## Features

- Live preview as you type
- Upload \`.md\` files
- Copy rendered HTML
- GitHub-flavored Markdown support

## Code Example

\`\`\`js
function greet(name) {
  return \`Hello, \${name}!\`;
}
\`\`\`

## Table Example

| Tool | Category | Free |
|------|----------|------|
| JSON Beautifier | JSON | ✅ |
| Markdown Viewer | Text | ✅ |
| Regex Tester | Dev | ✅ |

## Blockquote

> Markdown is a lightweight markup language with plain-text-formatting syntax.

---

**Enjoy writing in Markdown!**
`;

const MarkdownViewerPage = () => {
  const [mdInput, setMdInput] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    marked.setOptions({ breaks: true, gfm: true });
  }, []);

  const renderedHtml = mdInput
    ? marked.parse(mdInput)
    : '<p style="color:#aaa;text-align:center;margin-top:60px;">Markdown önizlemesi burada görünür...</p>';

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setMdInput(ev.target.result);
    reader.readAsText(file);
  };

  const copyHtml = () => {
    navigator.clipboard.writeText(marked.parse(mdInput)).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  const copyMarkdown = () => {
    navigator.clipboard.writeText(mdInput).then(() => {
      setCopySuccess('md');
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  const btn = (onClick, color, label) => (
    <button onClick={onClick} style={{
      padding: '8px 16px', background: color, color: 'white',
      border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px'
    }}>
      {label}
    </button>
  );

  return (
    <div>
      <SEO
        title="Markdown Viewer - Live Markdown Preview Online"
        description="Paste or upload Markdown and see a live rendered preview instantly. Free online Markdown editor with GitHub-Flavored Markdown support."
        keywords="markdown viewer, markdown preview, markdown editor, md viewer, markdown renderer, online markdown"
      />

      <div style={{ maxWidth: '95%', margin: '0 auto', padding: '20px' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>Markdown Viewer</h1>
        <p style={{ color: '#666', marginBottom: '20px' }}>
          Markdown yazın veya bir <code>.md</code> dosyası yükleyin — sağda canlı önizleme görün.
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
          {btn(() => fileInputRef.current.click(), '#2196F3', 'Dosya Yükle (.md)')}
          <input ref={fileInputRef} type="file" accept=".md,.markdown,.txt"
            onChange={handleFileUpload} style={{ display: 'none' }} />
          {btn(() => setMdInput(EXAMPLE_MD), '#9c27b0', 'Örnek Yükle')}
          {btn(copyHtml, '#607d8b', copySuccess === true ? 'Kopyalandı!' : 'HTML Kopyala')}
          {btn(copyMarkdown, '#00897b', copySuccess === 'md' ? 'Kopyalandı!' : 'Markdown Kopyala')}
          {btn(() => setMdInput(''), '#f44336', 'Temizle')}
        </div>

        <div style={{
          display: 'flex', gap: '20px',
          height: 'calc(100vh - 340px)', minHeight: '500px',
          overflow: 'hidden'
        }}>
          {/* Editor */}
          <div style={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
            <h3 style={{ fontSize: '1.1rem', marginBottom: '8px', color: '#374151', flexShrink: 0 }}>Markdown</h3>
            <textarea
              value={mdInput}
              onChange={(e) => setMdInput(e.target.value)}
              placeholder="Markdown yazın..."
              style={{
                flex: 1, width: '100%', boxSizing: 'border-box', minHeight: 0,
                fontFamily: 'monospace', fontSize: '14px',
                border: '1px solid #d1d5db', borderRadius: '6px',
                padding: '14px', resize: 'none', outline: 'none',
                background: '#fafafa', color: '#1f2937', lineHeight: '1.6',
                overflowY: 'auto'
              }}
              spellCheck={false}
            />
          </div>

          {/* Preview */}
          <div style={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
            <h3 style={{ fontSize: '1.1rem', marginBottom: '8px', color: '#374151', flexShrink: 0 }}>Önizleme</h3>
            <div
              className="md-preview"
              dangerouslySetInnerHTML={{ __html: renderedHtml }}
              style={{
                flex: 1, overflowY: 'auto', minHeight: 0,
                border: '1px solid #d1d5db', borderRadius: '6px',
                padding: '20px', background: 'white', boxSizing: 'border-box',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                fontSize: '15px', lineHeight: '1.7', color: '#1f2937'
              }}
            />
          </div>
        </div>
      </div>

      <style>{`
        .md-preview h1, .md-preview h2, .md-preview h3,
        .md-preview h4, .md-preview h5, .md-preview h6 {
          margin-top: 1.4em; margin-bottom: 0.5em; font-weight: 600; line-height: 1.3;
        }
        .md-preview h1 { font-size: 1.9em; border-bottom: 2px solid #e5e7eb; padding-bottom: 0.3em; }
        .md-preview h2 { font-size: 1.5em; border-bottom: 1px solid #e5e7eb; padding-bottom: 0.2em; }
        .md-preview h3 { font-size: 1.25em; }
        .md-preview p { margin: 0.8em 0; }
        .md-preview a { color: #3b82f6; text-decoration: none; }
        .md-preview a:hover { text-decoration: underline; }
        .md-preview code {
          background: #f3f4f6; padding: 2px 6px; border-radius: 4px;
          font-family: monospace; font-size: 0.9em; color: #d63384;
        }
        .md-preview pre {
          background: #1e293b; color: #e2e8f0; padding: 16px; border-radius: 8px;
          overflow-x: auto; font-size: 14px; line-height: 1.5;
          margin: 1em 0;
        }
        .md-preview pre code { background: none; padding: 0; color: inherit; font-size: inherit; }
        .md-preview blockquote {
          border-left: 4px solid #3b82f6; margin: 1em 0; padding: 8px 16px;
          background: #eff6ff; color: #374151; border-radius: 0 4px 4px 0;
        }
        .md-preview ul, .md-preview ol { padding-left: 1.8em; margin: 0.6em 0; }
        .md-preview li { margin: 0.3em 0; }
        .md-preview table {
          border-collapse: collapse; width: 100%; margin: 1em 0;
        }
        .md-preview th, .md-preview td {
          border: 1px solid #e5e7eb; padding: 8px 12px; text-align: left;
        }
        .md-preview th { background: #f9fafb; font-weight: 600; }
        .md-preview tr:nth-child(even) td { background: #f9fafb; }
        .md-preview hr { border: none; border-top: 2px solid #e5e7eb; margin: 1.5em 0; }
        .md-preview img { max-width: 100%; border-radius: 6px; }
        @media (max-width: 768px) {
          .md-preview { min-height: 300px; }
        }
      `}</style>
    </div>
  );
};

export default MarkdownViewerPage;
